// ---------------------------------------------------------------------------
// Element-based claims: the arithmetic between a CAD take-off row and the
// quantity a task orders.
//
// The CAD records several measures for the same element (a wall carries both a
// side area and a concrete volume), so a material is never "in" one unit. A
// claim names the ELEMENTS the task covers (by IFC GlobalId, per element type)
// and its quantity is the sum of those elements' OWN measures in whichever
// unit was chosen — never an average: a task that covers only the small slabs
// orders the small slabs' volume, not a share of the big ones. `'pieces'` is
// exactly 1 per element.
//
// Pure functions — no React, no store; unit-tested in isolation.
// ---------------------------------------------------------------------------

import { unitKeyOf } from '@d-orca/shared/client/logistics';
import type { ITakeoffElement, ITakeoffMaterial, TakeoffUnitKey } from '@interfaces/ICadTakeoff';
import type { IClaimElements } from '@interfaces/IScheduler';

/** The implicit unit every take-off row can be claimed in: one element, one
 *  piece. Not a `TakeoffUnitKey` — the CAD never reports it, it is the row's
 *  own `elementCount`. */
export const PIECES_UNIT_KEY = 'pieces';

/** A unit a CAD claim can be expressed in: whatever the geometry reports, plus
 *  pieces. Anything else the user could pick (kg, bags…) is not one of these. */
export type ClaimUnitKey = TakeoffUnitKey | typeof PIECES_UNIT_KEY;

/** The measure keys a take-off row can carry. */
export const TAKEOFF_UNIT_KEYS: readonly TakeoffUnitKey[] = ['m', 'm2', 'm3'];

/** Fixed presentation order for the unit list: the CAD's own measures from
 *  one dimension up, pieces last. */
export const CLAIM_UNIT_ORDER: readonly ClaimUnitKey[] = ['m', 'm2', 'm3', PIECES_UNIT_KEY];

/** Tie-break order for the DEFAULT unit only — the opposite way round. When
 *  two measures cover the same number of elements, the higher-dimensional one
 *  is the better guess at what the material is bought in: an element that
 *  reports a volume on every piece is almost always bulk stock (concrete,
 *  fill), while a length or an area alone is what remains meaningful for
 *  pipe or sheet goods. Pieces stays last: it is always available, so it
 *  would otherwise win every tie. Ruled 2026-09-08. */
const DEFAULT_UNIT_TIE_ORDER: readonly ClaimUnitKey[] = ['m3', 'm2', 'm', PIECES_UNIT_KEY];

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * The claim unit key a stored unit stands for — a unit key ('m3') or its
 * label in either language ('Cu. m.', '㎥'), through the shared identity rule
 * (`unitKeyOf`), so a claim seeded from a key-stored stock row resolves too.
 * Null for every other unit in the vocabulary (kg, bags, rolls…): the CAD
 * cannot measure it, so no element arithmetic applies.
 */
export function unitKeyOfLabel(label: string): ClaimUnitKey | null {
  const key = unitKeyOf(label);
  return CLAIM_UNIT_ORDER.find((k) => k === key) ?? null;
}

/**
 * How much of `key` ONE element holds — its own measure, exactly 1 for
 * `'pieces'`. Null when the element never reported that measure (and for a
 * null key, so callers with an off-vocabulary unit need no guard of their
 * own).
 */
export function elementMeasure(el: ITakeoffElement, key: ClaimUnitKey | null): number | null {
  if (key === null) return null;
  if (key === PIECES_UNIT_KEY) return 1;
  return el.measures[key] ?? null;
}

/**
 * How many of the row's elements actually reported `key` — the coverage a
 * row shows when it is short of `elementCount`. Every element counts for
 * `'pieces'` (and for a null key, which has no measure to qualify), so only
 * a real measure can come in under `elementCount`.
 */
export function measuredCount(row: ITakeoffMaterial, key: ClaimUnitKey | null): number {
  if (key === null || key === PIECES_UNIT_KEY) return row.elementCount;
  return row.measures[key]?.elements ?? 0;
}

/** Can any element of this row be measured in `key`? False for a null key
 *  (kg, bags…) and for a measure none of the row's elements reported. */
export function canMeasure(row: ITakeoffMaterial, key: ClaimUnitKey | null): boolean {
  return key !== null && measuredCount(row, key) > 0;
}

/**
 * Every unit the material can be claimed in — each key at least one row can
 * measure, plus `'pieces'`, in `CLAIM_UNIT_ORDER`. Never empty: pieces always
 * qualifies, even for a material whose elements carry no geometry at all.
 */
export function unitKeysFor(rows: ITakeoffMaterial[]): ClaimUnitKey[] {
  return CLAIM_UNIT_ORDER.filter((k) => k === PIECES_UNIT_KEY || rows.some((r) => canMeasure(r, k)));
}

/**
 * The unit a freshly picked material starts in: the CAD measure MOST of its
 * elements report, so the field opens on something the material as a whole
 * shares rather than a measure only a few elements carry. "Most" is a
 * majority of the elements — a handful without a quantity set (four beams
 * out of three thousand concrete elements, on the K280 model) must not hand
 * the default to pieces, which every element trivially reports. Pieces is
 * the default only when no measure covers more than half. Ties between
 * measures break by `DEFAULT_UNIT_TIE_ORDER` (volume first).
 */
export function defaultUnitKey(rows: ITakeoffMaterial[]): ClaimUnitKey {
  const pieces = rows.reduce((sum, r) => sum + r.elementCount, 0);
  let best: ClaimUnitKey = PIECES_UNIT_KEY;
  let bestScore = -1;
  const offered = unitKeysFor(rows);
  for (const key of DEFAULT_UNIT_TIE_ORDER.filter((k) => k !== PIECES_UNIT_KEY && offered.includes(k))) {
    const score = rows.reduce((sum, r) => sum + measuredCount(r, key), 0);
    if (score > bestScore) {
      best = key;
      bestScore = score;
    }
  }
  return bestScore * 2 > pieces ? best : PIECES_UNIT_KEY;
}

/** The grouped row's elements for `kind`; empty when the CAD no longer holds
 *  that type on the floor. */
export function elementsOf(rows: ITakeoffMaterial[], kind: string): ITakeoffElement[] {
  return rows.find((r) => r.kind === kind)?.elements ?? [];
}

/** The globalIds a claim holds for `kind`; empty when it holds none. */
export function chosenIds(elements: IClaimElements[], kind: string): string[] {
  return elements.find((e) => e.kind === kind)?.globalIds ?? [];
}

/** Which floor an element of a grouped row sits on — `ITaskStock.floorOf`,
 *  null for an element on no mapped storey. */
export type FloorOf = (globalId: string) => string | null;

/** The distinct floors `ids` sit on, first-seen order, elements on no mapped
 *  storey skipped — what an entry's `floorIds` records. */
function floorsOf(ids: string[], floorOf: FloorOf): string[] {
  const floors: string[] = [];
  for (const id of ids) {
    const floor = floorOf(id);
    if (floor !== null && !floors.includes(floor)) floors.push(floor);
  }
  return floors;
}

/**
 * `elements` with `kind` set to `ids` — a new array, never a mutation. An
 * empty `ids` drops the entry rather than storing an empty row, so a saved
 * claim only ever names the types it actually takes; an existing type keeps
 * its position and a new one is appended. The entry also records the floors
 * those elements were chosen from, which is what the delivery drawer names.
 */
export function withChosen(
  elements: IClaimElements[],
  kind: string,
  ids: string[],
  floorOf: FloorOf,
): IClaimElements[] {
  if (ids.length === 0) return elements.filter((e) => e.kind !== kind);
  const entry: IClaimElements = { kind, globalIds: ids, floorIds: floorsOf(ids, floorOf) };
  if (elements.some((e) => e.kind === kind)) {
    return elements.map((e) => (e.kind === kind ? entry : e));
  }
  return [...elements, entry];
}

/** What `takenElements` reads off another task's claim. */
export interface IHoldingClaim {
  taskName: string;
  elements: IClaimElements[];
}

/**
 * Every element OTHER tasks already hold on this material + floor, mapped to
 * the holding task's name (the first to name an element keeps it, should two
 * tasks somehow both claim one). What comes off a type's free elements before
 * this claim gets to pick any.
 */
export function takenElements(otherClaims: IHoldingClaim[]): Map<string, string> {
  const taken = new Map<string, string>();
  for (const other of otherClaims) {
    for (const entry of other.elements) {
      for (const id of entry.globalIds) {
        if (!taken.has(id)) taken.set(id, other.taskName);
      }
    }
  }
  return taken;
}

/** Σ `elementMeasure` over the elements in `ids`, unrounded — an element
 *  without the measure contributes 0. */
function sumMeasures(elements: ITakeoffElement[], ids: ReadonlySet<string>, key: ClaimUnitKey | null): number {
  let total = 0;
  for (const el of elements) {
    if (ids.has(el.globalId)) total += elementMeasure(el, key) ?? 0;
  }
  return total;
}

/** What a set of chosen elements comes to in `key`, rounded to 1 decimal —
 *  the element list's running total. */
export function chosenQuantity(
  elements: ITakeoffElement[],
  chosen: ReadonlySet<string>,
  key: ClaimUnitKey | null,
): number {
  return round1(sumMeasures(elements, chosen, key));
}

/**
 * What the claim adds up to in `key`: the sum of every chosen element's own
 * measure, rounded once at the end. An element the rows no longer hold, or
 * one without that measure, contributes nothing — a claim in a unit the CAD
 * cannot measure at all (`key === null`) therefore comes to 0.
 */
export function claimQuantity(
  rows: ITakeoffMaterial[],
  elements: IClaimElements[],
  key: ClaimUnitKey | null,
): number {
  let total = 0;
  for (const entry of elements) {
    total += sumMeasures(elementsOf(rows, entry.kind), new Set(entry.globalIds), key);
  }
  return round1(total);
}

/**
 * The total still claimable in `key` — the free elements of every row that
 * can measure it, summed. Null when NO row can (an off-vocabulary unit, a
 * measure the CAD never took, or a free-text claim with no rows at all),
 * which is what turns the ceiling read-out off.
 */
export function claimableCeiling(
  rows: ITakeoffMaterial[],
  taken: ReadonlyMap<string, string>,
  key: ClaimUnitKey | null,
): number | null {
  let total = 0;
  let measured = false;
  for (const row of rows) {
    if (!canMeasure(row, key)) continue;
    measured = true;
    const free = new Set(row.elements.filter((el) => !taken.has(el.globalId)).map((el) => el.globalId));
    total += sumMeasures(row.elements, free, key);
  }
  return measured ? round1(total) : null;
}

/** One type some of whose chosen elements the task can no longer have. */
export interface IElementConflict {
  kind: string;
  /** Chosen elements that another task now holds, or that are no longer in
   *  the CAD. */
  conflicting: number;
  /** The first holding task's name; null when every conflicting element is
   *  simply gone from the model. */
  takenBy: string | null;
}

/** The chosen ids of one entry split into what still fits and what does not
 *  — the one walk both `elementConflicts` and `fitElements` are built on. */
function splitEntry(
  rows: ITakeoffMaterial[],
  entry: IClaimElements,
  taken: ReadonlyMap<string, string>,
): { kept: string[]; conflict: IElementConflict | null } {
  const present = new Set(elementsOf(rows, entry.kind).map((el) => el.globalId));
  const kept: string[] = [];
  let conflicting = 0;
  let takenBy: string | null = null;
  for (const id of entry.globalIds) {
    const holder = taken.get(id);
    if (holder === undefined && present.has(id)) {
      kept.push(id);
      continue;
    }
    conflicting += 1;
    if (takenBy === null && holder !== undefined) takenBy = holder;
  }
  return { kept, conflict: conflicting === 0 ? null : { kind: entry.kind, conflicting, takenBy } };
}

/** Every type the claim no longer fully has, in the claim's own order:
 *  chosen elements another task holds, or that a CAD revision removed. Empty
 *  when everything still fits. */
export function elementConflicts(
  rows: ITakeoffMaterial[],
  elements: IClaimElements[],
  taken: ReadonlyMap<string, string>,
): IElementConflict[] {
  const conflicts: IElementConflict[] = [];
  for (const entry of elements) {
    const { conflict } = splitEntry(rows, entry, taken);
    if (conflict !== null) conflicts.push(conflict);
  }
  return conflicts;
}

/** The claim's elements minus the conflicting ones, entries left empty
 *  dropped. What the CAD-changed banner's "Set" action writes back. Each
 *  surviving entry's `floorIds` is recomputed from what it kept — a revision
 *  that took a whole floor's elements away takes that floor off the entry
 *  too. */
export function fitElements(
  rows: ITakeoffMaterial[],
  elements: IClaimElements[],
  taken: ReadonlyMap<string, string>,
  floorOf: FloorOf,
): IClaimElements[] {
  const fitted: IClaimElements[] = [];
  for (const entry of elements) {
    const { kept } = splitEntry(rows, entry, taken);
    if (kept.length > 0) fitted.push({ kind: entry.kind, globalIds: kept, floorIds: floorsOf(kept, floorOf) });
  }
  return fitted;
}

/** One element as the list renders it. */
export interface IElementMember {
  el: ITakeoffElement;
  /** The task holding it, null when free. */
  taken: string | null;
  chosen: boolean;
}

/** Elements of one type with the same quantity in the chosen unit. */
export interface IElementGroup {
  /** The shared `elementMeasure`, rounded to 1 decimal; null when these
   *  elements carry no such measure. */
  qty: number | null;
  /** Free members first (input order kept), taken ones last. */
  members: IElementMember[];
}

/** Every element sharing one `typeName`, folded into groups. */
export interface IElementSection {
  /** Null when the elements have no type — the caller names the kind. */
  typeName: string | null;
  groups: IElementGroup[];
  /** Elements in the section, across every group. */
  count: number;
}

const hasFree = (g: IElementGroup): boolean => g.members.some((m) => m.taken === null);

/** Groups by quantity descending, elements without the measure after every
 *  measured group; a group with no free member after every group that has
 *  one, whatever its quantity. */
function compareGroups(a: IElementGroup, b: IElementGroup): number {
  const freeOrder = Number(hasFree(b)) - Number(hasFree(a));
  if (freeOrder !== 0) return freeOrder;
  if (a.qty === null || b.qty === null) return Number(a.qty === null) - Number(b.qty === null);
  return b.qty - a.qty;
}

/**
 * The element list's model: sections by `typeName` in first-seen order; in a
 * section, elements with the same rounded measure fold into one group;
 * groups sorted by quantity descending with the all-taken ones last; in a
 * group, free members first and taken ones last, input order otherwise kept.
 */
export function elementGroups(
  elements: ITakeoffElement[],
  taken: ReadonlyMap<string, string>,
  chosen: ReadonlySet<string>,
  key: ClaimUnitKey | null,
): IElementSection[] {
  const sections = new Map<string | null, Map<number | null, IElementGroup>>();
  for (const el of elements) {
    const groups = sections.get(el.typeName) ?? new Map<number | null, IElementGroup>();
    sections.set(el.typeName, groups);
    const measure = elementMeasure(el, key);
    const qty = measure === null ? null : round1(measure);
    const group = groups.get(qty) ?? { qty, members: [] };
    groups.set(qty, group);
    group.members.push({ el, taken: taken.get(el.globalId) ?? null, chosen: chosen.has(el.globalId) });
  }
  return [...sections].map(([typeName, groups]) => {
    const sorted = [...groups.values()].sort(compareGroups);
    for (const group of sorted) {
      group.members = [
        ...group.members.filter((m) => m.taken === null),
        ...group.members.filter((m) => m.taken !== null),
      ];
    }
    return { typeName, groups: sorted, count: sorted.reduce((n, g) => n + g.members.length, 0) };
  });
}

/** One floor's share of a type's elements — the element list's band, and
 *  the types block's per-floor split. */
export interface IFloorBand {
  floorId: string;
  floorName: string;
  /** In model order, as the type's elements had them. */
  elements: ITakeoffElement[];
}

/**
 * `elements` split by the floor each sits on (`floorOf`, from the take-off
 * row it came from), in floor order; a floor holding none of them is left
 * out, and an element on no floor — or on one outside `floorIds` — goes
 * nowhere. Empty `floorIds` (the whole model) means no bands at all.
 */
export function floorBands(
  elements: ITakeoffElement[],
  floorIds: string[],
  floorNames: string[],
  floorOf: FloorOf,
): IFloorBand[] {
  const byFloor = new Map<string, ITakeoffElement[]>(floorIds.map((id) => [id, []]));
  for (const el of elements) {
    const floor = floorOf(el.globalId);
    if (floor !== null) byFloor.get(floor)?.push(el);
  }
  return floorIds.flatMap((floorId, i) => {
    const members = byFloor.get(floorId)!;
    return members.length === 0 ? [] : [{ floorId, floorName: floorNames[i]!, elements: members }];
  });
}

/** Which piece of a split element this is: Revit exports a floor drawn as
 *  several separate outlines as one IFC slab PER outline, every piece carrying
 *  the same Revit id (`tag`) — "Floor:D=20:2415194", "…:2", "…:3" on K280. */
export interface ITagPart {
  index: number;
  count: number;
}

/**
 * `globalId` → its part number for every element whose `tag` at least one
 * OTHER element of the list shares, in input order (which is the order the
 * exporter numbered them in). An element with a unique tag, or no tag, is not
 * in the map — its Revit id alone tells it apart.
 */
export function tagParts(elements: ITakeoffElement[]): Map<string, ITagPart> {
  const byTag = new Map<string, ITakeoffElement[]>();
  for (const el of elements) {
    if (el.tag === null) continue;
    byTag.set(el.tag, [...(byTag.get(el.tag) ?? []), el]);
  }
  const parts = new Map<string, ITagPart>();
  for (const group of byTag.values()) {
    if (group.length < 2) continue;
    group.forEach((el, i) => parts.set(el.globalId, { index: i + 1, count: group.length }));
  }
  return parts;
}

/**
 * The selection with exactly `count` of `group`'s FREE members chosen — the
 * group row's counter. Raising it adds the next unchosen free members in model
 * order; lowering it drops chosen ones from the end; ticks made on specific
 * members inside the group survive either way. Clamped to the free count.
 */
export function withGroupCount(chosen: ReadonlySet<string>, group: IElementGroup, count: number): Set<string> {
  const free = group.members.filter((m) => m.taken === null).map((m) => m.el.globalId);
  const target = Math.max(0, Math.min(free.length, Math.floor(count)));
  const next = new Set(chosen);
  const picked = free.filter((id) => next.has(id));
  for (const id of picked.slice(target)) next.delete(id);
  for (const id of free.filter((id) => !next.has(id)).slice(0, Math.max(0, target - picked.length))) next.add(id);
  return next;
}
